import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @file 桌面端应用的整体框架布局
 */
import { useOnUpdate } from '@seiue/util';
import Layout from 'antd/es/layout';
import Menu from 'antd/es/menu';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { ThemeProvider, useGlobalState } from '../theme/Provider';
import { LoadingBar } from './components/LoadingBar';
import { MenuItem } from './components/MenuItem';
import { SeiueLogo } from './components/SeiueLogo';
import { SiderMenuTitle } from './components/SiderMenuTitle';
import './styles.less';
import { getMenuAbsolutePath, getMenuKey } from './utils';
// 需要用到 theme 里的一些全局样式
const MenuGlobalStyle = createGlobalStyle `
  // 菜单弹出窗口的样式, 它创建在body下需要用到全局样式
  .app-layout-menu-popup.ant-menu-light {
    // 菜单项默认样式
    .ant-menu-submenu-title,
    .ant-menu-item, .ant-menu-item a {
     color: ${props => props.theme.text._1};
      .ant-menu-inline-collapsed-noicon,
      .anticon,
      .ant-menu-submenu-arrow {
        color: #5D6169;
      }
    }
    // 菜单项选中时的一些样式
    .ant-menu-submenu-selected .ant-menu-submenu-title,
    .ant-menu-item-selected, .ant-menu-item-selected a {
      color: ${props => props.theme.brand.text0};
      .ant-menu-inline-collapsed-noicon,
      .anticon,
      .ant-menu-submenu-arrow {
        color: ${props => props.theme.brand.text0};
      }
    }
  }
`;
export const AppLayout = ({ activeMenuPath, isCollapsed: isCollapsedProp, onIsCollapsedChange, logoSlot, headerLeft, headerRight, menus, children, siderHeader, siderFooter, siderMenuTitle, onOpenKeysChanged, navbarBackgroundColor = '#1c212a', sliderBackgroundColor = '#fff', showLoadingBar, showCollapsedTrigger = true, headerClassName, siderClassName, }) => {
    const [themeColor] = useGlobalState('themeColor');
    const [isCollapsedState, setIsCollapsedState] = useState(false);
    const isCollapsed = isCollapsedProp !== null && isCollapsedProp !== void 0 ? isCollapsedProp : isCollapsedState;
    // 更新收起展开状态的方法
    const toggleIsCollapsed = useCallback((val) => {
        // 传了 val 就是用 val，没有就用!isCollapsed
        const value = val === undefined ? !isCollapsed : val;
        if (isCollapsedProp == null) {
            setIsCollapsedState(value);
        }
        onIsCollapsedChange === null || onIsCollapsedChange === void 0 ? void 0 : onIsCollapsedChange(value);
    }, [isCollapsed, isCollapsedProp, onIsCollapsedChange]);
    const [openKeys, setOpenKeys] = useState([]);
    const activeMenuAbsoluteKeys = useMemo(() => {
        const abs = getMenuAbsolutePath(menus || [], activeMenuPath);
        return abs === null || abs === void 0 ? void 0 : abs.map(item => getMenuKey(item));
    }, [activeMenuPath, menus]);
    useEffect(() => {
        setOpenKeys(() => {
            const abs = getMenuAbsolutePath(menus || [], activeMenuPath);
            return abs === null || abs === void 0 ? void 0 : abs.map(item => getMenuKey(item));
        });
    }, [activeMenuPath, menus]);
    useOnUpdate(() => {
        onOpenKeysChanged === null || onOpenKeysChanged === void 0 ? void 0 : onOpenKeysChanged(openKeys);
    }, [openKeys]);
    const renderMenuItem = React.useCallback((item, index) => (_jsx(MenuItem, { item: item, toggleIsCollapsed: () => toggleIsCollapsed(), isCollapsed: isCollapsed, activeMenuAbsoluteKeys: activeMenuAbsoluteKeys, sliderBackgroundColor: sliderBackgroundColor, 
        // 定义了侧边栏标题的情况下，首个菜单不再展示 icon
        collapseIcon: !siderMenuTitle && index === 0 && showCollapsedTrigger }, index)), [
        toggleIsCollapsed,
        isCollapsed,
        activeMenuAbsoluteKeys,
        sliderBackgroundColor,
        siderMenuTitle,
        showCollapsedTrigger,
    ]);
    return (_jsxs(Layout, Object.assign({ style: { height: '100vh', minWidth: '1024px' } }, { children: [_jsxs(Layout.Header, Object.assign({ style: {
                    backgroundColor: navbarBackgroundColor,
                    color: '#fff',
                    boxShadow: '0 2px 12px 0 rgba(50,56,66,0.06)',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 24px',
                    lineHeight: '52px',
                    height: 52,
                }, className: headerClassName }, { children: [_jsxs("div", Object.assign({ style: {
                            display: 'flex',
                            alignItems: 'center',
                            flexGrow: 1,
                        } }, { children: [logoSlot || _jsx(SeiueLogo, {}, void 0), headerLeft] }), void 0), headerRight] }), void 0), _jsxs(Layout, { children: [!menus ? null : (
                    // TODO 未加入主题色配置
                    _jsx(ThemeProvider, Object.assign({ themeColor: themeColor }, { children: _jsxs(Layout.Sider, Object.assign({ "data-test-id": "\u4FA7\u8FB9\u680F", collapsible: true, collapsed: isCollapsed, trigger: null, width: '240', 
                            // sm 屏幕 (< 576px) 时菜单默认收起
                            breakpoint: 'sm', onBreakpoint: x => {
                                toggleIsCollapsed(x);
                            }, style: { zIndex: 102, background: sliderBackgroundColor }, collapsedWidth: 57, className: siderClassName }, { children: [_jsx(MenuGlobalStyle, {}, void 0), _jsxs(SiderContentWrapper, { children: [_jsxs(SiderContentStyled, Object.assign({ menuDefaultHeight: 36, sliderBackgroundColor: sliderBackgroundColor }, { children: [siderHeader, siderMenuTitle && (_jsx(SiderMenuTitle, { title: siderMenuTitle, isCollapsed: isCollapsed, toggleIsCollapsed: () => toggleIsCollapsed(), showCollapsedTrigger: showCollapsedTrigger }, void 0)), _jsx(Menu, Object.assign({ inlineIndent: 20, mode: "inline", className: "web-layout-menu", style: {
                                                        background: sliderBackgroundColor,
                                                    }, selectedKeys: activeMenuPath ? [activeMenuPath] : [], openKeys: openKeys, onOpenChange: keys => setOpenKeys(keys) }, { children: menus.map(renderMenuItem) }), void 0)] }), void 0), _jsx(SiderFooter, { children: siderFooter }, void 0)] }, void 0)] }), void 0) }), void 0)), _jsx(Layout, { children: _jsxs(Layout.Content, Object.assign({ style: {
                                display: 'flex',
                                flexDirection: 'column',
                            } }, { children: [_jsx(LoadingBar, { loading: !!showLoadingBar }, void 0), children] }), void 0) }, void 0)] }, void 0)] }), void 0));
};
const SiderContentWrapper = styled.div `
  max-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: white;
  padding-top: 16px;

  &&& .ant-menu-dark.ant-menu-inline .ant-menu-item::after {
    border-right: 4px solid ${p => p.theme.brand._1};
  }
`;
const SiderContentStyled = styled.div `
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  // 需要提高优先级的通用样式
  &&& {
    .ant-menu-light {
      // 菜单选中时的一些样式
      .ant-menu-sub .ant-menu-submenu-selected .ant-menu-submenu-title,
      .ant-menu-sub .ant-menu-item-selected,
      .ant-menu-sub .ant-menu-item-selected a,
      .ant-menu-submenu-selected .ant-menu-submenu-title,
      .ant-menu-item-selected,
      .ant-menu-item-selected a {
        color: ${props => props.theme.brand.text0};

        .ant-menu-inline-collapsed-noicon,
        .anticon,
        .ant-menu-submenu-arrow {
          color: ${props => props.theme.brand.text0};
        }

        &:hover {
          .ant-menu-inline-collapsed-noicon,
          .anticon,
          .ant-menu-submenu-arrow {
            color: ${props => props.theme.brand.text0};
          }
        }
      }
    }
  }

  // light通用样式
  .ant-menu-light {
    // 菜单文本和icon的默认颜色
    .ant-menu-submenu-title,
    .ant-menu-item,
    .ant-menu-item a {
      color: ${props => props.theme.text._1};
      .ant-menu-inline-collapsed-noicon,
      .anticon,
      .ant-menu-submenu-arrow {
        color: #5d6169;
      }
    }

    // 背景相关的处理
    .ant-menu-item,
    .ant-menu-submenu-title {
      &:hover {
        background-color: #f3f4f8;
      }
    }
    .ant-menu-item-selected {
      background: #f3f4f8 !important;
    }
    .ant-menu-inline.ant-menu-sub {
      background: #fff;
    }
  }

  // light的默认样式
  .ant-menu-light:not(.ant-menu-inline-collapsed) {
    padding: 0 12px;
    // submenu子菜单默认样式
    .ant-menu-sub {
      // 子菜单文本使用较浅的色号，来突出子菜单的层级
      .ant-menu-item,
      .ant-menu-item a,
      .ant-menu-submenu-title {
        color: ${props => props.theme.text._2};
        .ant-menu-inline-collapsed-noicon,
        .anticon,
        .ant-menu-submenu-arrow {
          color: #5d6169;
        }
      }
    }
    // 隐藏菜单选中状态下 右侧的的蓝色边框
    .ant-menu-item,
    .ant-menu-inline.ant-menu-sub .ant-menu-item {
      &::after {
        display: none;
      }
    }
  }

  // light收起时的样式
  .ant-menu-light.ant-menu-inline-collapsed {
    padding: 0 8px;
    // 菜单项在hover时，文本和icon的一些样式
    .ant-menu-submenu-title,
    .ant-menu-item,
    .ant-menu-item a {
      &:hover {
        color: ${props => props.theme.brand.text0};
        .ant-menu-inline-collapsed-noicon,
        .anticon,
        .ant-menu-submenu-arrow {
          color: ${props => props.theme.brand.text0};
        }
      }
    }
  }
`;
const SiderFooter = styled.div ``;
//# sourceMappingURL=data:application/json;base64,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